import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import usePagination from "../hooks/pagination";
import preIcon from "../assets/img/previous.svg";
import nextIcon from "../assets/img/next.svg";
import { useTranslation } from "react-i18next";
import { FiSearch, FiPlus, FiMinus, FiX } from "react-icons/fi";
import { AWS_URL } from "../services/http.service";

// ── Image resolver ─────────────────────────────────────────────
const resolveImage = (product) => {
  if (!product) return "";

  const candidates = [
    product.itemPicture,
    product.item_picture,
    product.productImage,
    product.product_image,
    product.image,
    product.imageUrl,
    product.image_url,
    product.thumbnail,
    product.thumbnailUrl,
    product.img,
    product.photo,
  ];

  if (Array.isArray(product.images) && product.images.length) {
    const first = product.images[0];
    if (typeof first === "string") candidates.push(first);
    else if (first && typeof first === "object")
      candidates.push(first.url || first.imageUrl || first.path);
  }

  const found = candidates.find((c) => typeof c === "string" && c.trim());
  if (!found) return "";

  if (/^https?:\/\//i.test(found) || found.startsWith("data:")) return found;

  const base = (AWS_URL || "").replace(/\/+$/, "");
  const path = found.startsWith("/") ? found : `/${found}`;
  return base ? `${base}${path}` : path;
};

const ProductSelection = ({
  products,
  handleSelectedProducts,
  handleIsOpen,
  isPitara,
  isServices,
  isRawMaterials = false,
  isPurchase = false,
}) => {
  const navigate = useNavigate();
  const { bussinessUuid } = useParams();
  const { t } = useTranslation();

  const [selections, setSelections] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [search, setSearch] = useState("");
  const [brokenImages, setBrokenImages] = useState({});
  const pageLimit = 24;

  const cardRefs = useRef({});
  const selectBtnRef = useRef(null);
  const searchInputRef = useRef(null);

  const getProductId = (product) => {
    if (isPitara === true) return product._id;
    if (isServices === true) return product.serviceId;
    if (isRawMaterials === true) return product.rawMaterialId;
    return product.productId;
  };

  const getProductName = (product) =>
    isServices
      ? product.serviceName
      : isRawMaterials
      ? product.rawMaterialName
      : product.productName;

  useEffect(() => {
    const next = {};
    products.forEach((p) => {
      const id = getProductId(p);
      next[id] = {
        checked: false,
        quantity: 1,
        taxIncluded: p.taxIncluded || false,
      };
    });
    setSelections(next);
    setBrokenImages({});
  }, [products]);

  const catKey = isRawMaterials ? "rawMaterialCategory" : "productCategory";
  const subKey = isRawMaterials ? "rawMaterialSubCategory" : "productSubCategory";

  const categories = useMemo(
    () => [...new Set(products.map((p) => p[catKey]).filter(Boolean))],
    [products, catKey]
  );

  const subcategories = useMemo(() => {
    if (!selectedCategory) return [];
    return [
      ...new Set(
        products
          .filter((p) => p[catKey] === selectedCategory)
          .map((p) => p[subKey])
          .filter(Boolean)
      ),
    ];
  }, [products, catKey, subKey, selectedCategory]);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (selectedCategory && p[catKey] !== selectedCategory) return false;
      if (selectedSubcategory && p[subKey] !== selectedSubcategory) return false;
      if (search) {
        const name = (getProductName(p) || "").toLowerCase();
        if (!name.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [products, selectedCategory, selectedSubcategory, search, catKey, subKey, isServices, isRawMaterials]);

  const { pageNumber, pageCount, pageData, nextPage, previousPage } =
    usePagination(filteredProducts, pageLimit);

  const displayProducts = pageData();

  const toggleSelect = useCallback((product) => {
    const id = getProductId(product);
    setSelections((prev) => {
      const current =
        prev[id] || { checked: false, quantity: 1, taxIncluded: !!product.taxIncluded };
      return { ...prev, [id]: { ...current, checked: !current.checked } };
    });
  }, [isPitara, isServices, isRawMaterials]);

  const setQuantity = useCallback((product, qty) => {
    const id = getProductId(product);
    const max = Number(product.quantity ?? Infinity);
    const safeQty = Math.max(1, Math.min(qty, max || 1));
    setSelections((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || { checked: false, taxIncluded: !!product.taxIncluded }),
        checked: true,
        quantity: safeQty,
      },
    }));
  }, [isPitara, isServices, isRawMaterials]);

  // Only ↑ / ↓ / + / − / Escape on a card — Enter is NOT handled here.
  const handleCardKeyDown = (e, product) => {
    const stock = Number(product.quantity ?? 0);
    const outOfStock = stock === 0;
    if (outOfStock) return;

    const id = getProductId(product);
    const currentQty = selections[id]?.quantity || 1;

    if (e.key === "ArrowUp" || e.key === "+" || e.key === "=") {
      e.preventDefault();
      setQuantity(product, currentQty + 1);
    } else if (e.key === "ArrowDown" || e.key === "-") {
      e.preventDefault();
      if (currentQty > 1) setQuantity(product, currentQty - 1);
    } else if (e.key === "Escape") {
      e.currentTarget.blur();
    } else if (e.key === " " || e.key === "Spacebar") {
      // Space toggles selection on a focused card
      e.preventDefault();
      toggleSelect(product);
    }
  };

  // Same for the qty input — Enter is NOT intercepted here either,
  // so it bubbles up to the global handler that triggers the Select button.
  const handleQtyInputKeyDown = (e, product) => {
    if (
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "+" ||
      e.key === "-" ||
      e.key === "="
    ) {
      e.preventDefault();
      handleCardKeyDown(e, product);
    } else if (e.key === "Escape") {
      e.currentTarget.blur();
    }
    // Enter → let it bubble (global handler will trigger Select)
  };

  const handleSelect = () => {
    const selected = products
      .filter((p) => selections[getProductId(p)]?.checked)
      .map((p) => {
        const sel = selections[getProductId(p)];
        return { ...p, quantity: sel.quantity || 1, taxIncluded: sel.taxIncluded || false };
      });
    handleSelectedProducts(selected);
    handleIsOpen();
  };

  // Keep latest handler in a ref so the global listener never goes stale
  const handleSelectRef = useRef(handleSelect);
  useEffect(() => {
    handleSelectRef.current = handleSelect;
  });

  const selectedCount = Object.values(selections).filter((s) => s.checked).length;

  // ── Global Enter → click Select button ─────────────────────────────
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Enter" || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;

      const el = document.activeElement;
      const tag = el?.tagName;
      const isTextInput =
        tag === "INPUT" &&
        !["checkbox", "radio", "button", "submit"].includes(el?.type);
      const isTextArea = tag === "TEXTAREA";
      const isContentEditable = el?.isContentEditable;

      // If user is typing in the search box or any other text field,
      // Enter should submit the current filter instead of selecting all.
      if (el === searchInputRef.current) return; // let search handle Enter natively
      if (isTextArea || isContentEditable) return;
      // If focus is inside a qty number input — allow Enter to trigger Select too.
      // (That's the intent: Enter finalizes.)
      if (isTextInput && el?.dataset?.qtyinput !== "true") return;

      if (selectedCount === 0) return;
      e.preventDefault();
      selectBtnRef.current?.click();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedCount]);

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="mb-2 flex items-center gap-2">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Type here to search [F5]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-md text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Category chips */}
      <div className="mb-1.5 flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
        <button
          onClick={() => { setSelectedCategory(""); setSelectedSubcategory(""); }}
          className={`px-2.5 py-1 text-[11px] rounded-md whitespace-nowrap border ${
            !selectedCategory
              ? "bg-blue-600 text-white border-blue-600"
              : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-400"
          }`}
        >
          All Items
        </button>
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => { setSelectedCategory(c); setSelectedSubcategory(""); }}
            className={`px-2.5 py-1 text-[11px] rounded-md whitespace-nowrap border ${
              selectedCategory === c
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-400"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Subcategory chips */}
      {selectedCategory && subcategories.length > 0 && (
        <div className="mb-1.5 flex items-center gap-1.5 overflow-x-auto pb-1">
          <button
            onClick={() => setSelectedSubcategory("")}
            className={`px-2.5 py-1 text-[11px] rounded-md whitespace-nowrap border ${
              !selectedSubcategory
                ? "bg-emerald-600 text-white border-emerald-600"
                : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
            }`}
          >
            All Sub
          </button>
          {subcategories.map((s) => (
            <button
              key={s}
              onClick={() => setSelectedSubcategory(s)}
              className={`px-2.5 py-1 text-[11px] rounded-md whitespace-nowrap border ${
                selectedSubcategory === s
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 overflow-y-auto max-h-[58vh] pr-1">
        {displayProducts.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
            {t("FORM.NO_PRODUCTS_FOUND") || "No products found"}
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5">
            {displayProducts.map((product, idx) => {
              const id = getProductId(product);
              const sel = selections[id] || { checked: false, quantity: 1 };
              const name = getProductName(product);
              const stock = Number(product.quantity ?? 0);
              const outOfStock = stock === 0;
              const imgSrc = resolveImage(product);
              const showPlaceholder = !imgSrc || brokenImages[id];

              return (
                <div
                  key={id || idx}
                  ref={(el) => { cardRefs.current[id] = el; }}
                  tabIndex={outOfStock ? -1 : 0}
                  onKeyDown={(e) => handleCardKeyDown(e, product)}
                  onClick={() => !outOfStock && toggleSelect(product)}
                  className={`relative flex flex-col rounded border bg-white dark:bg-gray-800 overflow-hidden transition-all outline-none cursor-pointer focus:ring-2 focus:ring-blue-400 ${
                    sel.checked
                      ? "border-blue-500 ring-1 ring-blue-300 dark:ring-blue-800 shadow"
                      : "border-gray-200 dark:border-gray-700 hover:shadow"
                  } ${outOfStock ? "cursor-not-allowed" : ""}`}
                >
                  {/* X / check badge */}
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelect(product);
                    }}
                    disabled={outOfStock}
                    className={`absolute top-0.5 right-0.5 z-10 w-4 h-4 rounded-full flex items-center justify-center text-[8px] shadow ${
                      outOfStock
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : sel.checked
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-white/95 text-gray-500 hover:bg-red-50 hover:text-red-600"
                    }`}
                    title={outOfStock ? "Out of stock" : sel.checked ? "Remove" : "Select"}
                  >
                    {sel.checked ? "✓" : <FiX className="w-2.5 h-2.5" />}
                  </button>

                  {/* Small suppressed image box */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!outOfStock) toggleSelect(product);
                    }}
                    className={`relative w-full h-14 bg-gray-50 dark:bg-gray-900 flex items-center justify-center cursor-pointer overflow-hidden ${
                      outOfStock ? "opacity-50" : ""
                    }`}
                  >
                    {showPlaceholder ? (
                      <div className="text-lg text-gray-300 dark:text-gray-600">📦</div>
                    ) : (
                      <img
                        src={imgSrc}
                        alt={name}
                        loading="lazy"
                        className="max-w-full max-h-full object-contain p-0.5"
                        onError={() =>
                          setBrokenImages((prev) => ({ ...prev, [id]: true }))
                        }
                      />
                    )}
                    {outOfStock && (
                      <span className="absolute bottom-0 left-0 text-[8px] bg-red-600 text-white px-1 rounded-tr">
                        Out
                      </span>
                    )}
                  </div>

                  {/* Name */}
                  <div className="px-1 pt-0.5 text-[9px] font-medium text-gray-800 dark:text-gray-100 text-center leading-tight line-clamp-2 min-h-[22px]">
                    {name}
                  </div>

                  {/* Price */}
                  <div className="px-1 text-center text-[9px] text-gray-500 dark:text-gray-400">
                    ₹{product.salePrice || 0}
                  </div>

                  {/* Stepper */}
                  <div className="p-1 mt-auto">
                    <div className="flex items-center justify-between border border-gray-300 dark:border-gray-600 rounded overflow-hidden h-7">
                      <button
                        type="button"
                        tabIndex={-1}
                        disabled={outOfStock || !sel.checked || sel.quantity <= 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          setQuantity(product, sel.quantity - 1);
                        }}
                        className="w-6 h-full flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
                      >
                        <FiMinus className="w-3 h-3" />
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        data-qtyinput="true"
                        value={sel.quantity}
                        disabled={outOfStock}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!sel.checked) toggleSelect(product);
                        }}
                        onFocus={() => {
                          if (!sel.checked) toggleSelect(product);
                        }}
                        onKeyDown={(e) => handleQtyInputKeyDown(e, product)}
                        onChange={(e) => {
                          const v = parseInt(e.target.value.replace(/\D/g, "") || "1", 10);
                          setQuantity(product, isNaN(v) ? 1 : v);
                        }}
                        className="w-full min-w-[24px] text-center text-[11px] font-medium py-0 bg-transparent dark:text-white focus:outline-none disabled:opacity-50"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        disabled={
                          outOfStock ||
                          !sel.checked ||
                          (stock > 0 && sel.quantity >= stock)
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          setQuantity(product, sel.quantity + 1);
                        }}
                        className="w-6 h-full flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
                      >
                        <FiPlus className="w-3 h-3" />
                      </button>
                    </div>
                    {stock > 0 && (
                      <div className="text-[8px] text-center text-gray-400 mt-0.5">
                        Stock: {stock}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-2 pt-2 border-t dark:border-gray-700 gap-2">
        <div className="flex items-center text-gray-600 dark:text-gray-400">
          <button onClick={previousPage} disabled={pageNumber === 0} className="disabled:opacity-40">
            <img src={preIcon} className="cursor-pointer mr-1 w-4" alt="previous" />
          </button>
          <span className="mx-2 text-[11px]">
            {filteredProducts.length > 0
              ? `Page ${pageNumber + 1} of ${pageCount} • ${filteredProducts.length} items`
              : "No data"}
          </span>
          <button onClick={nextPage} disabled={pageNumber >= pageCount - 1} className="disabled:opacity-40">
            <img src={nextIcon} className="cursor-pointer ml-1 w-4" alt="next" />
          </button>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {selectedCount > 0 && (
            <span className="text-[11px] text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded">
              {selectedCount} selected
            </span>
          )}
          <button
            ref={selectBtnRef}
            onClick={handleSelect}
            disabled={selectedCount === 0}
            title="Press Enter to confirm"
            className={`w-full sm:w-auto px-5 py-1.5 rounded-md font-semibold text-xs transition-all ${
              selectedCount > 0
                ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow"
                : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            }`}
          >
            {t("MENU.SELECT") || "Select"} {selectedCount > 0 ? `(${selectedCount})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSelection;